import React from 'react'
import { ExternalLink, Lock, Clock, Mic } from 'lucide-react'
import HeatBadge from './HeatBadge'
import CoverageBadge from './CoverageBadge'
import ExpandableSummary from './ExpandableSummary'

const categoryConfig = {
  markets: { label: 'MARKETS', color: 'text-cyan-400' },
  tech: { label: 'TECH', color: 'text-violet-400' },
  world: { label: 'WORLD', color: 'text-rose-400' },
  sports: { label: 'SPORTS', color: 'text-emerald-400' },
  popculture: { label: 'CULTURE', color: 'text-amber-400' },
  general: { label: 'NEWS', color: 'text-gray-400' }
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return ''
  const age = (Date.now() - new Date(dateStr).getTime()) / 60000
  if (age < 15) return 'Just now'
  if (age < 60) return `${Math.floor(age)}m ago`
  if (age < 1440) return `${Math.floor(age / 60)}h ago`
  return 'Yesterday'
}

function CompactLinks({ links }) {
  if (!links || links.length === 0) return null
  return (
    <div className="flex items-center gap-2 md:gap-1.5 flex-wrap">
      {links.slice(0, 3).map((link, i) => (
        <a
          key={i}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`
            inline-flex items-center gap-0.5 text-[10px] md:text-[9px] px-2 py-1 md:px-1.5 md:py-0.5 rounded
            font-medium uppercase tracking-wider transition-colors
            ${link.is_paywalled
              ? 'text-gray-600 active:text-gray-400'
              : 'text-gray-500 active:text-emerald-400'
            }
          `}
        >
          {link.is_paywalled && <Lock className="w-2.5 h-2.5 md:w-2 md:h-2" />}
          {link.name}
          <ExternalLink className="w-2.5 h-2.5 md:w-2 md:h-2 opacity-40" />
        </a>
      ))}
    </div>
  )
}

export default function StoryCard({ story, maxHeat }) {
  const cat = categoryConfig[story.category] || categoryConfig.general
  const headline = story.headline || story.title

  return (
    <article className="group p-3.5 md:p-4 rounded-none md:rounded-xl bg-white/[0.02] active:bg-white/[0.05] md:hover:bg-white/[0.05] transition-all story-card border-b md:border-0 border-white/5 last:border-0 -mx-3 md:mx-0 px-3 md:px-4">
      <div className="flex items-center gap-2 mb-1.5 md:mb-1">
        <span className={`text-[10px] font-bold tracking-[0.1em] ${cat.color}`}>
          {cat.label}
        </span>
        {story.first_seen && (
          <span className="text-[10px] md:text-[9px] text-gray-600 flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" />
            {formatRelativeTime(story.first_seen)}
          </span>
        )}
        <CoverageBadge score={story.coverage_score} matches={story.reference_matches} size="sm" />
        {story.podcast_mentions?.length > 0 && (
          <span className="text-[10px] md:text-[9px] text-fuchsia-400 flex items-center gap-0.5 whitespace-nowrap">
            <Mic className="w-2.5 h-2.5" />
            {story.podcast_mentions.join(', ')}
          </span>
        )}
        {story.is_major && (
          <span className="text-[10px] md:text-[9px] font-bold tracking-[0.1em] text-emerald-400">HOT</span>
        )}
      </div>

      <h3 className="text-[15px] md:text-base font-semibold text-white mb-1.5 leading-snug group-hover:text-emerald-400 transition-colors line-clamp-3 md:line-clamp-2">
        {headline}
      </h3>

      {story.summary && (
        <p className="text-[13px] md:text-xs text-gray-500 leading-relaxed mb-1.5 line-clamp-2">
          {story.summary}
        </p>
      )}

      <ExpandableSummary bullets={story.bullet_points} />

      <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5 md:mt-2">
        <CompactLinks links={story.free_links} />
        <HeatBadge score={story.heat_score} maxScore={maxHeat} size="sm" />
      </div>
    </article>
  )
}
